import { useMemo } from 'react'

import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import { PLATFORM, STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'

export type SidebarView =
  | 'Checklist'
  | 'Content'
  | 'Experience'
  | 'Batch Update Select'
  | 'Batch Update Complete'
export type SidebarTab = 'Checklist' | 'Content' | 'Experience'

interface SidebarDetails {
  view: SidebarView
  lastUpdatedTimestamp?: number
}

const EXPIRATION_MS = 1000 * 60 * 60

export async function setSidebarView(
  platform: PLATFORM,
  sidebarView: SidebarView,
): Promise<void> {
  const currentSidebarView = await localStorage.getSingle(
    STORAGE_KEY.SIDEBAR_VIEW,
  )
  return chrome.storage.local.set({
    [STORAGE_KEY.SIDEBAR_VIEW]: {
      ...(currentSidebarView || {}),
      [platform]: {
        view: sidebarView,
        lastUpdatedTimestamp: Date.now(),
      },
    },
  })
}

export function parseSidebarDetails(
  value: SidebarDetails | string | undefined,
  expiration_ms: number,
): SidebarView | undefined {
  if (!value) {
    return undefined
  }

  if (typeof value === 'string') {
    return value as SidebarView
  }

  if (
    !value.lastUpdatedTimestamp ||
    Date.now() > value.lastUpdatedTimestamp + expiration_ms
  ) {
    return undefined
  }

  return value.view
}

export function useSidebarView(
  platform: PLATFORM | null | undefined,
): SidebarView | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.SIDEBAR_VIEW],
  })

  const platformValue = useMemo(() => {
    if (!platform || !isLoaded || !data) {
      // Still loading
      return undefined
    }

    const blob: SidebarDetails | string | undefined =
      data?.[STORAGE_KEY.SIDEBAR_VIEW]?.[platform]

    return parseSidebarDetails(blob, EXPIRATION_MS)
  }, [platform, isLoaded, data])

  return platformValue || 'Checklist'
}
